import { makeAutoObservable, runInAction } from "mobx";
import { Nullable } from "@gemlightbox/core-kit";

import { getDiffInDays, getExpiredDate } from "src/common/helpers/subscriptions.helpers";
import {
  additionalLegacySubGbAddonId,
  additionalLegacySubGbAddonMonthlyPriceId,
  additionalLegacySubGbAddonYearlyPriceId,
  additionalSubGbAddonId,
  additionalSubGbAddonMonthlyPriceId,
  additionalSubGbAddonYearlyPriceId,
  newPROPlanId,
  proPlanId,
  proPlanMonthlyPriceId,
  proPlanYearlyPriceId,
  oldStarterPlanId,
  starterPlanId,
  starterPlanMonthlyPriceId,
  starterPlanYearlyPriceId,
  proActivatePlanMonthlyPriceId,
  proPlanIdV2,
  proPlanIdV2Legacy,
  proV2PlanMonthlyPriceId,
  proV2PlanYearlyPriceId,
  proV2LegacyPlanMonthlyPriceId,
  proV2LegacyPlanYearlyPriceId,
  proPlanPriceIds,
} from "src/containers/settings/subscriptions/subscriptions.constants";
import {
  FormattedBillingInfo,
  deleteCard,
  deleteSubscription,
  getCards,
  getSubscription,
  postSubscriptionUpcomingInvoicePreview,
  getSubscriptionPlans,
  putCard,
  putRenewSubscription,
  PostSubscriptionUpcomingInvoicePreviewData,
} from "src/api";
import { GetSubscriptionPlansResponse } from "src/api";
import { SubscriptionLimitsModel, CardModel } from "src/models";
import { RootStore } from "../root.store";
import { ErrorCodes } from "src/constants";
import { createRequest } from "urql";
import { graphqlClient } from "src/api";
import { USER_INFO_SUBSCRIPTION } from "src/api/graphql-api/user/user.gql";

export class SubscriptionsStore {
  constructor(private readonly _rootStore: RootStore) {
    makeAutoObservable(this);
  }

  private _paymentCardsList: CardModel[] = [];
  private _defaultPaymentCardIndex = -1;
  private _paymentCardsLoading = false;
  private set _setPaymentCardList(cards: CardModel[]) {
    this._paymentCardsList = cards;
    this._defaultPaymentCardIndex = cards.findIndex((card) => card.isDefault);
  }

  private _isLoadingSubscription = false;
  public get isLoadingSubscription() {
    return this._isLoadingSubscription;
  }

  private _subscription: Nullable<any> = null;
  public get subscription() {
    return this._subscription;
  }

  private _organization: Nullable<any> = null;
  public get organization() {
    return this._organization;
  }

  private _isLoadingPlans = false;
  public get isLoadingPlans() {
    return this._isLoadingPlans;
  }

  private _subscriptionPlans: GetSubscriptionPlansResponse[] = [];
  public get subscriptionPlans() {
    return this._subscriptionPlans;
  }

  private _isSubscribed = false;
  public get isSubscribed() {
    return this._isSubscribed;
  }

  private _isStarter = false;
  public get isStarter() {
    return this._isStarter;
  }

  public get isFreeSubscription() {
    return !this._isSubscribed;
  }

  public get isCustomSubscription() {
    return this.isSubscribed && this.subscription.product.unit_label === "enterprise";
  }

  public get isProSubscription() {
    return (
      this.subscription?.plan?.product === proPlanId ||
      this.subscription?.plan?.product === newPROPlanId ||
      this.subscription?.plan?.product === proPlanIdV2 ||
      this.subscription?.plan?.product === proPlanIdV2Legacy
    );
  }

  public get isProSubscriptionV2() {
    return (
      this.subscription?.plan?.product === proPlanIdV2 ||
      this.subscription?.plan?.product === proPlanIdV2Legacy
    );
  }

  public get isProSubscriptionV2Legacy() {
    return this.subscription?.plan?.product === proPlanIdV2Legacy;
  }

  public get isProMonthlySubscription() {
    return (
      this.isProSubscription &&
      !this.isFreeSubscription &&
      !this.isStarterSubscription &&
      !this.isCustomSubscription &&
      this.subscription?.plan?.interval === "month"
    );
  }

  public get isProYearlySubscription() {
    return (
      this.isProSubscription &&
      !this.isFreeSubscription &&
      !this.isStarterSubscription &&
      !this.isCustomSubscription &&
      this.subscription?.plan?.interval === "year"
    );
  }

  public get isProLifetimeSubscription() {
    // const coupon = this.subscription?.discount?.coupon;
    // return coupon?.valid && coupon?.duration === "forever" && coupon?.percent_off === 100;
    return this.isProLifetimeByOff100 || this.isProLifetime;
  }

  public get isProLifetimeByOff100() {
    return !!this.subscription?.isProLifetimeByOff100;
  }

  public get isProLifetime() {
    return !!this.subscription?.isProLifetime;
  }

  public get isOldStarterSubscription() {
    return this.subscription?.plan?.product === oldStarterPlanId;
  }

  public get isStarterSubscription() {
    return (
      this.subscription?.plan?.product === starterPlanId ||
      this.subscription?.plan?.product === oldStarterPlanId
    );
  }

  public get isStarterMonthlySubscription() {
    return (
      this.isStarterSubscription &&
      !this.isFreeSubscription &&
      !this.isProSubscription &&
      !this.isCustomSubscription &&
      this.subscription?.plan?.interval === "month"
    );
  }

  public get isStarterYearlySubscription() {
    return (
      this.isStarterSubscription &&
      !this.isFreeSubscription &&
      !this.isProSubscription &&
      !this.isCustomSubscription &&
      this.subscription?.plan?.interval === "year"
    );
  }

  public get isCancellingSubscription() {
    return (
      this.subscription &&
      !!this.subscription.cancel_at &&
      !this._rootStore.stores.userStore?.isShopifyUser
    );
  }

  public get starterSubscriptionInterval() {
    return this.isStarterMonthlySubscription
      ? "monthly"
      : this.isStarterYearlySubscription
      ? "yearly"
      : undefined;
  }

  public get proSubscriptionInterval() {
    return this.isProLifetimeSubscription
      ? "lifetime"
      : this.isProYearlySubscription
      ? "yearly"
      : this.isProMonthlySubscription
      ? "monthly"
      : undefined;
  }

  public get currentPlan() {
    const currentPlanId = this.subscription?.plan?.product;

    return this.subscriptionPlans.find(({ plan_id }) => plan_id === currentPlanId);
  }

  public get proPlan() {
    return this.subscriptionPlans.find(({ plan_id }) => plan_id === proPlanId);
  }

  public get proPlanV2() {
    return this.subscriptionPlans.find(({ plan_id }) => plan_id === proPlanIdV2);
  }

  public get proPlanV2Legacy() {
    return this.subscriptionPlans.find(({ plan_id }) => plan_id === proPlanIdV2Legacy);
  }

  public get starterPlan() {
    return this.subscriptionPlans.find(({ plan_id }) => plan_id === starterPlanId);
  }

  public get subGbAddonsAmount(): number {
    return (
      this.subscription?.additional?.find(({ price }: any) =>
        [additionalSubGbAddonId, additionalLegacySubGbAddonId].includes(price.product),
      )?.quantity || 0
    );
  }

  public get planInterval() {
    if (this.isProLifetimeSubscription) return "lifetime";
    if (this.subscription?.plan?.interval === "month") return "monthly";
    if (this.subscription?.plan?.interval === "year") return "yearly";
  }

  public get proPlanPrices() {
    const plans = [this.proPlan, this.proPlanV2, this.proPlanV2Legacy];
    const findPriceAmount = (ids: string[]) => {
      for (const plan of plans) {
        const amount = plan?.prices.find(({ id }: any) => ids.includes(id))?.amount;
        if (typeof amount === "number") return amount;
      }
      return 0;
    };

    const monthlyPrice = findPriceAmount([
      proPlanMonthlyPriceId,
      proV2PlanMonthlyPriceId,
      proV2LegacyPlanMonthlyPriceId,
    ]);
    const monthlyActivatePrice = findPriceAmount([proActivatePlanMonthlyPriceId]);
    const yearlyPrice = findPriceAmount([
      proPlanYearlyPriceId,
      proV2PlanYearlyPriceId,
      proV2LegacyPlanYearlyPriceId,
    ]);
    return { monthlyPrice, monthlyActivatePrice, yearlyPrice };
  }

  public get starterPlanPrices() {
    const monthlyPrice =
      this.starterPlan?.prices.find(({ id }: any) => id === starterPlanMonthlyPriceId)?.amount || 0;
    const yearlyPrice =
      this.starterPlan?.prices.find(({ id }: any) => id === starterPlanYearlyPriceId)?.amount || 0;
    return { monthlyPrice, yearlyPrice };
  }

  public get subaccountAddonPrices() {
    const additionalId = this.isProSubscriptionV2Legacy
      ? additionalLegacySubGbAddonId
      : additionalSubGbAddonId;
    const addonPlans = this.subscriptionPlans.filter(
      ({ plan_id }: any) => plan_id === additionalId,
    );
    const findAddonPrice = (ids: string[]) => {
      for (const plan of addonPlans) {
        const amount = plan?.prices.find(({ id }: any) => ids.includes(id))?.amount;
        if (typeof amount === "number") return amount;
      }
      return 0;
    };

    const monthlyPrice = findAddonPrice([
      additionalSubGbAddonMonthlyPriceId,
      additionalLegacySubGbAddonMonthlyPriceId,
    ]);
    const yearlyPrice = findAddonPrice([
      additionalSubGbAddonYearlyPriceId,
      additionalLegacySubGbAddonYearlyPriceId,
    ]);

    return { monthlyPrice, yearlyPrice };
  }

  public get subaccountAddonLimitStorage() {
    const additionalId = this.isProSubscriptionV2Legacy
      ? additionalLegacySubGbAddonId
      : additionalSubGbAddonId;
    const plan = this.subscriptionPlans.find(({ plan_id }: any) => plan_id === additionalId);
    const limitStorage = Number(plan?.metadata?.limit_storage || 0);

    return limitStorage;
  }

  public get limits(): SubscriptionLimitsModel {
    return (
      this._subscription?.limits || {
        countGPTDescription: 0,
        countGPTPostGeneration: 0,
        count_removal_background: 0,
        ar_count: 0,
        availableRetouch: 0,
      }
    );
  }

  public get storageLimit() {
    return this.limits?.limit_storage || 3;
  }

  public get subaccountsLimit() {
    return this.limits.subaccounts || 0;
  }

  public get getExpireDay() {
    return () => getDiffInDays(this.subscription);
  }

  public get getExpireDate() {
    return (onlyCurrentPeriod?: boolean) => getExpiredDate(this.subscription, onlyCurrentPeriod);
  }

  public get paymentCardList() {
    return this._paymentCardsList;
  }

  public get paymentCardsLoading() {
    return this._paymentCardsLoading;
  }

  /* Requests ↓ */
  public async loadSubscription(retryAttempted = false) {
    runInAction(() => {
      this._isLoadingSubscription = true;
    });

    const { success, error } = await getSubscription.getRequest().fetch();

    if (error && error?.originalError?.code !== ErrorCodes.SUBSCRIPTION_NOT_FOUND) {
      if (!retryAttempted) {
        setTimeout(() => {
          this.loadSubscription(true);
        }, 500);
        return;
      }
    }

    runInAction(() => {
      this._isLoadingSubscription = false;

      if (success) {
        success.plan =
          success?.items?.data?.find((item: any) => proPlanPriceIds.includes(item?.plan?.id))
            ?.plan || success.plan;
        success.additional = success?.items?.data || success.additional;
        this._subscription = success;
        this._isSubscribed = true;
        this._isStarter = success.product.name.toLowerCase() === "starter";
      } else {
        this._subscription = error?.originalError;
        this._isSubscribed = false;
        this._isStarter = false;
      }
    });
  }

  public async loadOrganization() {
    try {
      const res = await graphqlClient
        .executeQuery(createRequest(USER_INFO_SUBSCRIPTION, {}))
        .toPromise();
      const organization = res?.data?.userInfo?.organization;
      runInAction(() => {
        this._organization = organization;
      });
    } catch (e) {
      console.warn("loadOrganization", e);
    }
  }

  public async loadSubscriptionPlans() {
    runInAction(() => {
      this._isLoadingPlans = true;
    });

    const { success } = await getSubscriptionPlans.getRequest().fetch();

    runInAction(() => {
      if (success) this._subscriptionPlans = success;
      this._isLoadingPlans = false;
    });
  }

  public async removeSubscription() {
    const { success } = await deleteSubscription.getRequest().fetch();

    runInAction(async () => {
      if (success) {
        await this.loadSubscription();
        await this.loadSubscriptionPlans();
      }
    });
  }

  public async renewSubscription() {
    const { success } = await putRenewSubscription.getRequest().fetch();

    runInAction(async () => {
      if (success) {
        await this.loadSubscription();
        await this.loadSubscriptionPlans();
      }
    });
  }

  public async loadPaymentCards() {
    if (this._paymentCardsLoading) return;

    runInAction(() => {
      this._paymentCardsLoading = true;
    });

    const { success } = await getCards.getRequest().fetch();

    runInAction(() => {
      if (success) this._setPaymentCardList = success;
      this._paymentCardsLoading = false;
    });
  }

  public async changeDefaultCard(cardId: CardModel["id"], cardIndex: number) {
    if (this._paymentCardsLoading) return;

    runInAction(() => {
      this._paymentCardsLoading = true;
    });

    this._paymentCardsList[cardIndex].isDefault = true;
    if (this._defaultPaymentCardIndex > -1) {
      this._paymentCardsList[this._defaultPaymentCardIndex].isDefault = false;
    }

    const { details } = await putCard.getRequest({ data: { card: cardId } }).fetch();

    runInAction(() => {
      if (details.isSuccess) {
        this._defaultPaymentCardIndex = cardIndex;
      } else {
        this._paymentCardsList[cardIndex].isDefault = false;
        if (this._defaultPaymentCardIndex > -1) {
          this._paymentCardsList[this._defaultPaymentCardIndex].isDefault = true;
        }
      }
      this._paymentCardsLoading = false;
    });
  }

  public async deletePaymentCard(cardId: string) {
    const { details } = await deleteCard.getRequest({ params: { cardId } }).fetch();

    runInAction(async () => {
      if (details.isSuccess) {
        this._setPaymentCardList = this.paymentCardList.filter((card) => {
          return card.id !== cardId;
        });
      }
    });
  }

  private _formattedBillingInfo?: FormattedBillingInfo;
  public get formattedBillingInfo() {
    return this._formattedBillingInfo;
  }

  public calculateCost(priceID: string, quantity: number) {
    const yearlyAddonPriceIds = [
      additionalSubGbAddonYearlyPriceId,
      additionalLegacySubGbAddonYearlyPriceId,
    ];
    const planProductId = this.subscription?.plan?.product;
    const planPriceIds =
      planProductId === proPlanIdV2Legacy
        ? {
            monthly: proV2LegacyPlanMonthlyPriceId,
            yearly: proV2LegacyPlanYearlyPriceId,
          }
        : planProductId === proPlanIdV2
        ? {
            monthly: proV2PlanMonthlyPriceId,
            yearly: proV2PlanYearlyPriceId,
          }
        : {
            monthly: proPlanMonthlyPriceId,
            yearly: proPlanYearlyPriceId,
          };
    const proPlanPriceId = yearlyAddonPriceIds.includes(priceID)
      ? planPriceIds.yearly
      : planPriceIds.monthly;
    const data = {
      additionals: [
        {
          price: priceID,
          quantity: quantity,
        },
      ],
    } as PostSubscriptionUpcomingInvoicePreviewData;
    if (!this.isProLifetimeSubscription) {
      data.main = {
        price: proPlanPriceId,
        quantity: 1,
      };
    }

    const result = postSubscriptionUpcomingInvoicePreview.getRequest({ data }).fetch();
    result
      .then(({ success }) => {
        if (success) {
          this._formattedBillingInfo = {
            current_amount_due: success.current_amount_due / 100,
            next_amount_due: success.next_amount_due / 100,
            next_billing_date: new Date(success.next_billing_date * 1000),
            period_amount_due: success.period_amount_due / 100,
          } as FormattedBillingInfo;
        } else {
          this._formattedBillingInfo = undefined;
        }
      })
      .catch(() => (this._formattedBillingInfo = undefined));
  }
  /* Requests ↑ */
}
